import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Category } from '../types';
import { categoriesApi } from '../services/mybudgetApi';
import { useAuth } from './AppContext';

interface CategoriesContextValue {
  categories: Category[];
  loading: boolean;
  refresh: () => Promise<void>;
  getCategoryById: (id: number) => Category | undefined;
  getDefaultCategoryId: () => number | undefined;
}

const CategoriesContext = createContext<CategoriesContextValue | null>(null);

export function CategoriesProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!user) {
      setCategories([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      setCategories(await categoriesApi.list());
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const getCategoryById = useCallback(
    (id: number) => categories.find((c) => c.id === id),
    [categories],
  );

  const getDefaultCategoryId = useCallback(() => {
    const house = categories.find((c) => c.slug === 'house');
    return house?.id ?? categories[0]?.id;
  }, [categories]);

  const value = useMemo(
    () => ({
      categories,
      loading,
      refresh,
      getCategoryById,
      getDefaultCategoryId,
    }),
    [categories, loading, refresh, getCategoryById, getDefaultCategoryId],
  );

  return (
    <CategoriesContext.Provider value={value}>
      {children}
    </CategoriesContext.Provider>
  );
}

export function useCategories(): CategoriesContextValue {
  const ctx = useContext(CategoriesContext);
  if (!ctx) {
    throw new Error('useCategories must be used within CategoriesProvider');
  }
  return ctx;
}
